import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getStoredConsent,
  setAnalyticsConsent,
  initAnalyticsFromStoredConsent,
  trackNetworkJourney,
} from "@/lib/analytics";

/**
 * Bottom-anchored consent banner shown on first visit until the reader
 * chooses. Renders nothing when consent has already been decided (either
 * direction). On accept, GA loads and the choice is persisted; on decline,
 * nothing loads and the banner stays away on subsequent visits.
 *
 * The consent choice is kept in localStorage under `ar-analytics-consent`.
 * Clearing that key surfaces the banner again on the next visit, which is
 * how a "reset preferences" link in the cookie policy would work.
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Rehydrate GA on returning visitors who granted consent previously.
    initAnalyticsFromStoredConsent();
    if (getStoredConsent() === null) setVisible(true);

    // Migrate the previous Plausible outbound-click handler to gtag. Fires
    // the network_journey event when consent has been granted; under denied
    // consent gtag no-ops meaningful hits, so nothing leaks.
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const a = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      try {
        const u = new URL(a.href);
        if (u.hostname === location.hostname) return;
        // Any link to a Shrink Network domain counts, UTM-tagged or not.
        trackNetworkJourney(u.hostname, u.searchParams.get("utm_campaign") || "");
      } catch {
        // ignore malformed URLs
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  if (!visible) return null;

  const onAccept = () => {
    setAnalyticsConsent("granted");
    setVisible(false);
  };
  const onDecline = () => {
    setAnalyticsConsent("denied");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie and analytics consent"
      className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6"
      style={{ background: "hsl(var(--card))", borderTop: "1px solid hsl(var(--border))" }}
      data-testid="consent-banner"
    >
      <div className="mx-auto max-w-4xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p
          className="text-sm leading-relaxed"
          style={{ color: "hsl(var(--foreground))" }}
        >
          We use Google Analytics to understand which pages readers find
          useful. Nothing is stored on your device unless you accept. You can
          change your choice any time from the{" "}
          <Link
            to="/cookie-policy"
            className="underline decoration-1 underline-offset-2 font-semibold"
            style={{ color: "hsl(var(--brand-text))" }}
          >
            cookie policy
          </Link>
          .
        </p>
        <div className="flex gap-3 shrink-0">
          <button
            type="button"
            onClick={onDecline}
            className="px-4 py-2 text-sm font-semibold"
            style={{
              background: "transparent",
              border: "1px solid hsl(var(--border))",
              color: "hsl(var(--foreground))",
              borderRadius: "var(--radius)",
            }}
            data-testid="consent-decline"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="px-4 py-2 text-sm font-semibold"
            style={{
              background: "hsl(var(--foreground))",
              color: "hsl(var(--background))",
              borderRadius: "var(--radius)",
            }}
            data-testid="consent-accept"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
